// Part of @roc/feature-track-record-v2 — isolated copy of the redesigned Track
// Record UI used only by crm-portal and roc-internal-portal. Logic matches the
// master TrackRecordEntities in @roc/feature-borrowers (expand, edit, upload) plus
// the internal verify/unverify-entity flow from newBorrowerTrackRecordCard.

import { Chip, FormControl, Grid, Link, Select, TextField, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import { Button, FileUploadModal, PDFPreview, StandardDialog } from '@roc/ui';
import { TrackRecordTable } from './trackRecordTable';
import { useState } from 'react';
import { BorrowerTrackRecordVerificationStore } from '@roc/feature-borrowers';

interface TrackRecordEntitiesProps {
  store: BorrowerTrackRecordVerificationStore;
  borrowerBaseballCardStore?: any;
  isInternal?: boolean;
}

export const TrackRecordEntities = observer(({
  store,
  borrowerBaseballCardStore,
  isInternal = false,
}: TrackRecordEntitiesProps) => {
  const [uploadDocument, setUploadDocument] = useState(false);
  const [currentRowId, setCurrentRowId] = useState(null);
  const [currentRowType, setCurrentRowType] = useState(null);

  // Verify / unverify entity (internal only) — mirrors newBorrowerTrackRecordCard.
  const [currentEntity, setCurrentEntity] = useState<any>(null);
  const [isVerifyEntityModalOpen, setIsVerifyEntityModalOpen] = useState(false);
  const [isUnverifyEntityModalOpen, setIsUnverifyEntityModalOpen] = useState(false);
  const [verificationSource, setVerificationSource] = useState<string>('');
  const [othersReason, setOthersReason] = useState('');
  const [unverifyReason, setUnverifyReason] = useState('');

  const trackRecordEntitiesHeaders = [
    { name: 'Name', width: '26%' },
    { name: 'State', width: '14%' },
    { name: 'Verification Source', width: '26%' },
    { name: 'Verification Status', width: '18%' },
  ];

  const openUploadDocument = async (id, type) => {
    setCurrentRowId(id);
    setCurrentRowType(type);
    setUploadDocument(true);
  };

  const handleUploadDocument = async (id, files, type) => {
    const taskEntityBody = {
      sectionProcess: 'TRACK_RECORD_REVIEW',
      objectType: 'BORROWER_ENTITY_BORROWER',
      taskTemplateType: 'BORROWER_ENTITY_BORROWER',
      objectId: id,
    };

    store.createTaskWithFile(taskEntityBody, files[0]);
  };

  const previewDocument = (id, name, fileExtension) => {
    store.downloadTaskFile(id, name, fileExtension);
  };

  const refreshEntities = () => {
    store.fetchBorrowerInfoAndEntities();
  };

  const handleOpenVerifyEntityModal = (entity) => {
    setCurrentEntity(entity);
    setVerificationSource('');
    setOthersReason('');
    setIsVerifyEntityModalOpen(true);
  };

  const handleCloseVerifyEntityModal = () => {
    setIsVerifyEntityModalOpen(false);
    setCurrentEntity(null);
    setVerificationSource('');
    setOthersReason('');
  };

  const handleVerifyEntity = async () => {
    await borrowerBaseballCardStore?.updateVerifyEntity(
      currentEntity.borrowerEntityBorrowerId,
      currentEntity.entityVerified === 'Yes',
      verificationSource,
      borrowerBaseballCardStore?.loanId,
      othersReason,
    );
    handleCloseVerifyEntityModal();
    refreshEntities();
  };

  const handleOpenUnverifyEntityModal = (entity) => {
    setCurrentEntity(entity);
    setUnverifyReason('');
    setIsUnverifyEntityModalOpen(true);
  };

  const handleCloseUnverifyEntityModal = () => {
    setIsUnverifyEntityModalOpen(false);
    setCurrentEntity(null);
    setUnverifyReason('');
  };

  // Remove via the authenticated baseball-card store (same path as verify)
  const handleRemoveEntity = async (entity) => {
    await borrowerBaseballCardStore?.removeEntity(entity.borrowerEntityBorrowerId);
    refreshEntities();
  };

  const handleUnverifyEntity = async () => {
    await borrowerBaseballCardStore?.updateVerifyEntity(
      currentEntity.borrowerEntityBorrowerId,
      currentEntity.entityVerified === 'Yes',
      unverifyReason,
      borrowerBaseballCardStore?.loanId,
    );
    handleCloseUnverifyEntityModal();
    refreshEntities();
  };

  const verifyDisabled = !verificationSource || (verificationSource === 'Other' && !othersReason);

  const trackRecordEntitiesRows = store.entities?.reduce((rows, entity) => {
    const isVerified = entity.entityVerified !== 'No';
    rows.push({
      id: entity.borrowerEntityBorrowerId,
      object: entity,
      type: 'entity',
      cells: [
        entity.entityName,
        entity.entityState,
        !entity.verificationSource || entity.verificationSource === 'null' ? '-' : entity.verificationSource,
        <Grid container justifyContent="center">
          <Chip
            label={isVerified ? 'Verified' : 'Not Verified'}
            size="small"
            style={{
              backgroundColor: isVerified ? 'green' : 'grey',
              color: 'white',
            }}
          />
        </Grid>,
      ],
      expandableContent: (
        <Grid container>
          <Grid item xs={12} style={{ marginBottom: '1rem' }}>
            <Typography variant="subtitle1">
              <strong>Documents</strong>
            </Typography>
            <Grid style={{ display: 'flex', flexDirection: 'column' }}>
              {store.currentTasks && store.currentTasks.length > 0
                ? store.currentTasks.map(task => {
                  if (task.originalFileName) {
                    return (
                      <Link
                        key={task.taskId}
                        onClick={() => previewDocument(task.taskId, task.originalFileName, task.fileExtension)}
                      >
                        {task.originalFileName}
                      </Link>
                    );
                  }
                  return null;
                })
                : 'No Records'}
            </Grid>
          </Grid>
        </Grid>
      ),
      onUploadDocument: () => openUploadDocument(entity.borrowerEntityBorrowerId, 'entity'),
      canVerify: isInternal && entity.entityVerified === 'No',
      onVerify: () => handleOpenVerifyEntityModal(entity),
      canUnverify: isInternal && entity.entityVerified === 'Yes',
      onUnverify: () => handleOpenUnverifyEntityModal(entity),
      canRemove: isInternal && entity.entityVerified === 'No' && entity.isEntityPresentInLoan !== 'true',
      onRemove: () => handleRemoveEntity(entity),
    });
    return rows;
  }, []);

  return (
    <Grid container direction="row" spacing={2}>
      <Grid item xs={12}>
        <TrackRecordTable
          isExpandable={true}
          store={store}
          headers={trackRecordEntitiesHeaders}
          rows={trackRecordEntitiesRows ?? []}
          enablePagination={(store.entities?.length ?? 0) > 5}
          borrowerBaseballCardStore={null}
        />
      </Grid>
      <FileUploadModal
        open={uploadDocument}
        title="Upload Document"
        onClose={() => setUploadDocument(false)}
        onUpload={(files) => handleUploadDocument(currentRowId, files, currentRowType)}
      />
      <PDFPreview
        open={store.isPdfPreviewModalOpen}
        title={store.pdfTitle}
        data={store.pdfData}
        headers={{}}
        fileName={store.pdfTitle}
        onClose={() => {
          store.pdfTitle = null;
          store.pdfData = null;
          store.isPdfPreviewModalOpen = false;
        }}
      />

      <StandardDialog
        open={isVerifyEntityModalOpen}
        maxWidth="sm"
        title="Verify Entity"
        handleClose={handleCloseVerifyEntityModal}
        dialogContent={
          <Grid container direction="column">
            <Grid>
              <Typography variant="body1" style={{ marginBottom: '25px' }}>
                What is the verification source ?
                <FormControl style={{ marginLeft: '10px', position: 'relative', bottom: '3px', marginRight: '5px' }}>
                  <Select
                    value={verificationSource}
                    native
                    onChange={(e) => setVerificationSource(e.target.value as string)}
                  >
                    <option aria-label="None" value="" />
                    <option value="Bizapedia">Bizapedia</option>
                    <option value="Open Corporates">Open Corporates</option>
                    <option value="Entity Docs">Entity Docs</option>
                    <option value="Other">Other</option>
                  </Select>
                </FormControl>
              </Typography>
              {verificationSource === 'Other' && (
                <>
                  <Typography>Please specify:</Typography>
                  <FormControl style={{ width: '100%', marginBottom: '16px', paddingRight: '64px' }}>
                    <TextField
                      variant="outlined"
                      id="entityVerificationOthersReason"
                      value={othersReason}
                      onChange={(e) => setOthersReason(e.target.value)}
                    />
                  </FormControl>
                </>
              )}
              <Typography variant="body1">
                Are you sure you want to mark this entity as verified ?
              </Typography>
            </Grid>
          </Grid>
        }
        dialogActions={
          <Grid container direction="row" justifyContent="flex-end">
            <Button testId="cancelVerifyEntity" style={{ marginRight: '10px' }} onClick={handleCloseVerifyEntityModal}>
              Cancel
            </Button>
            <Button testId="confirmVerifyEntity" disabled={verifyDisabled} color="primary" variant="outlined" onClick={handleVerifyEntity}>
              Verify
            </Button>
          </Grid>
        }
      />

      <StandardDialog
        open={isUnverifyEntityModalOpen}
        maxWidth="sm"
        title="Unverify Entity"
        handleClose={handleCloseUnverifyEntityModal}
        dialogContent={
          <Grid container direction="column">
            <Grid>
              <Typography>Please specify the reason:</Typography>
              <FormControl style={{ width: '100%', marginBottom: '16px', paddingRight: '64px' }}>
                <TextField
                  variant="outlined"
                  id="entityUnverificationReason"
                  value={unverifyReason}
                  onChange={(e) => setUnverifyReason(e.target.value)}
                />
              </FormControl>
              <Typography variant="body1">
                Are you sure you want to mark this entity as unverified ?
              </Typography>
            </Grid>
          </Grid>
        }
        dialogActions={
          <Grid container direction="row" justifyContent="flex-end">
            <Button testId="cancelUnverifyEntity" style={{ marginRight: '10px' }} onClick={handleCloseUnverifyEntityModal}>
              Cancel
            </Button>
            <Button testId="confirmUnverifyEntity" disabled={!unverifyReason} color="primary" variant="outlined" onClick={handleUnverifyEntity}>
              Unverify
            </Button>
          </Grid>
        }
      />
    </Grid>
  );
});
